// İzin (leave) hesaplamaları için paylaşımlı yardımcılar.
//
// Kök neden: `leave_request.reason_type` bazı kayıtlarda kanonik etiket
// ("Sosyal İzin", "Yıllık İzin") yerine makine slug'ı ("sosyal_izin",
// "yillik_izin") olarak saklanabiliyor (ör. doğrudan DB'ye yazan seed/backfill
// akışları `_normalize_reason_type`'ı atlıyor). UI kanonik etikete birebir
// eşitlik varsayınca sayaç/gösterim tutarsızlaşıyor:
//   - Gruplama tam eşleşme beklediği için slug'lı talepler listeden düşer,
//     ama "(N talep)" sayacı tüm talepleri sayar.
//   - Kalan gün, bir yüzeyde stale sayaç alanından, başka yüzeyde onaylı
//     taleplerden türetilince aynı personel iki farklı sayı gösterir.
// Bu yardımcılar reason_type'ı kanonikleştirip "kullanılan gün"ü tek noktadan
// hesaplayarak yüzeyler arası tutarlılığı garanti eder.

export const CANONICAL_LEAVE_TYPES = ["Yıllık İzin", "Sosyal İzin", "Rapor", "Diğer"];

/**
 * Slug/etiket/varyant fark etmeksizin reason_type'ı kanonik etikete çevirir.
 * Bilinmeyen değerler olduğu gibi döner (gösterimden asla düşmesin diye).
 */
export const canonicalizeReasonType = (raw) => {
  const s = (raw ?? "").toString().trim();
  const low = s.toLowerCase();
  if (low.includes("yıllık") || low.includes("yillik")) return "Yıllık İzin";
  if (low.includes("sosyal")) return "Sosyal İzin";
  if (low.includes("rapor")) return "Rapor";
  if (low.includes("diğer") || low.includes("diger")) return "Diğer";
  return s || "Diğer";
};

/**
 * Bir izin talebinin kaç gün "kullanıldığını" çözer.
 * leave_days_used_actual → workdays_used → total_days sırasıyla; hiçbiri
 * geçerli pozitif değilse tarih aralığından (dahil) gün sayısını hesaplar.
 * NOT: LeaveManagementPanel'deki resolveUsedDays ile birebir aynı mantık.
 */
export const resolveLeaveUsedDays = (lr) => {
  const raw = lr?.leave_days_used_actual ?? lr?.workdays_used ?? lr?.total_days;
  const n = Number(raw);
  if (Number.isFinite(n) && n > 0) return n;
  const s = lr?.start_date ? String(lr.start_date).slice(0, 10) : null;
  const e = (lr?.end_date || lr?.return_date) ? String(lr.end_date || lr.return_date).slice(0, 10) : s;
  if (!s || !e) return 0;
  const sd = new Date(s);
  const ed = new Date(e);
  if (Number.isNaN(sd.getTime()) || Number.isNaN(ed.getTime())) return 0;
  return Math.max(1, Math.floor((ed - sd) / (1000 * 60 * 60 * 24)) + 1);
};

/**
 * Yıllık izin kartında "(N iş günü düşer · hafta sonu/tatil hariç)" netlik
 * ipucu gösterilmeli mi?
 *
 * Kural: yalnız YILLIK izinde, backend `workdays_used`'ı geçerli-pozitif
 * verdiyse ve bu değer takvim gününden (`total_days`) FARKLIYSA gösterilir.
 * - reason_type slug ("yillik_izin") olabildiğinden kanonikleştirilir.
 * - Memur (civil servant) için workdays_used == total_days olduğundan
 *   koşul kendiliğinden false döner → ipucu gizlenir (yanlış bilgi verilmez).
 * - workdays_used yoksa/0 ise gizlenir; iş günü istemcide tatil setiyle yeniden
 *   hesaplanmaz (tek doğruluk kaynağı backend değeridir).
 */
export const shouldShowAnnualWorkdayHint = (request) => {
  if (!request) return false;
  if (canonicalizeReasonType(request.reason_type || request.leave_type) !== "Yıllık İzin") {
    return false;
  }
  const workdays = Number(request.workdays_used);
  if (!Number.isFinite(workdays) || workdays <= 0) return false;
  return workdays !== Number(request.total_days);
};

// Backend, kural dışı talebi işaretlerken o anki bildirim eşiğini de talebe
// "dondurur" (MIN_NOTICE_DAYS:<n>). Böylece Müdür eşiği sonradan değiştirse bile
// eski talep kendi dönemindeki kuralı anlatmaya devam eder.
const MIN_NOTICE_CODE_PREFIX = "MIN_NOTICE_DAYS:";
const ANNUAL_NOTICE_VIOLATION = "ANNUAL_MIN_2_DAYS_RULE_VIOLATION";
const SOCIAL_NOTICE_VIOLATION = "SOCIAL_MIN_1_DAY_RULE_VIOLATION";

/**
 * Bildirim süresi ihlali rozeti + açıklaması. Kart ve karar listesi özet satırı
 * AYNI kaynaktan beslensin diye burada; iki yerde ayrı metin kurmak (daha önce
 * bakiye tarafında yaşandı) er geç birbiriyle çelişen iki gösterim üretiyor.
 *
 * Döner: { label, note } veya kural ihlali yoksa null.
 */
export const buildNoticeRuleInfo = (request) => {
  const codes = Array.isArray(request?.review_reason_codes) ? request.review_reason_codes : [];
  const isAnnual = codes.includes(ANNUAL_NOTICE_VIOLATION);
  const isSocial = codes.includes(SOCIAL_NOTICE_VIOLATION);
  if (!isAnnual && !isSocial) return null;

  // Gerçekten geçmiş mi, yoksa yalnız aynı gün mü? (yarın için açılan talep
  // yanlışlıkla "geçmiş gün" etiketlenmesin diye start_date ile ayırt edilir)
  let isGenuinelyPast = false;
  if (request?.start_date) {
    const start = new Date(request.start_date);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    isGenuinelyPast = !Number.isNaN(start.getTime()) && start < today;
  }

  let frozenMinDays = null;
  for (const code of codes) {
    const text = String(code);
    if (text.startsWith(MIN_NOTICE_CODE_PREFIX)) {
      const parsed = Number(text.slice(MIN_NOTICE_CODE_PREFIX.length));
      if (Number.isFinite(parsed)) frozenMinDays = parsed;
      break;
    }
  }

  const minDays = frozenMinDays != null ? frozenMinDays : (isSocial ? 1 : 2);
  const typeLabel = isSocial ? "Sosyal izin" : "Yıllık izin";
  // Son karar makamı türe göre değişir: yıllık izin → Müdür, sosyal izin → MY.
  const finalAuthority = isSocial ? "Müdür Yardımcısı" : "Müdür";

  return {
    label: isGenuinelyPast ? "Geçmiş gün talebi" : "Aynı gün talebi",
    note:
      `${typeLabel}, izin başlangıç tarihinden en az ${minDays} gün önce talep edilmelidir. ` +
      `Bu talep süre şartına uymadığı için Formen ve Bölge Mühendisi atlanarak doğrudan ` +
      `Müdür Yardımcısı'nın özel değerlendirmesine gönderildi; son kararı ${finalAuthority} verir.`,
  };
};

/**
 * Bir vekile ait park kapsamı: `leave_request.park_substitutes` içinde bu kişiye
 * (önce id, yoksa isimle) atanmış park_id'lerle `parks` listesini daraltır.
 *
 * KRİTİK kapsam kuralı: per-park vekiller varken bir kişiye YALNIZ kendi parkları
 * gösterilmeli (Erdinç yalnız Başak'ı görmeli). `park_substitutes` boş/yoksa tek
 * vekil demektir → tüm parklar bu kişiye aittir, `parks` olduğu gibi döner.
 */
export const filterParksForSubstitute = (parkSubstitutes, parks, person = {}) => {
  const list = Array.isArray(parkSubstitutes) ? parkSubstitutes : [];
  const safeParks = Array.isArray(parks) ? parks : [];
  if (list.length === 0) return safeParks; // tek vekil → tüm parklar
  const wantId = String(person?.id || "").trim();
  const wantName = String(person?.name || "").trim().toLowerCase().replace(/\s+/g, " ");
  const myParkIds = new Set(
    list
      .filter((p) => {
        const sid = String(p?.substitute_id || p?.substitute_personnel_id || "").trim();
        if (wantId && sid && sid === wantId) return true;
        const sname = String(p?.substitute_name || p?.substitute_personnel_name || "")
          .trim()
          .toLowerCase()
          .replace(/\s+/g, " ");
        return Boolean(wantName && sname && sname === wantName);
      })
      .map((p) => String(p?.park_id || ""))
      .filter(Boolean)
  );
  return safeParks.filter((p) => myParkIds.has(String(p?.id || "")));
};

/**
 * Vekil adaylarını uygunluk (izinli/raporlu) bilgisiyle işaretler — CREATE ve
 * EDIT (Vekil Düzenle) ekranlarının TEK ORTAK kaynağı.
 *
 * Kök neden (bu fonksiyondan önce): create ekranı `is_unavailable` (tarih-aralığı
 * çakışması, backend eligibility) VEYA personelin `status === on_leave/on_report`
 * (halihazırda izinde) sinyalini birlikte kullanıyordu; edit ekranı YALNIZ map'i
 * kullanıyordu. Eligibility fetch boş/başarısız olduğunda ya da kişi o an izindeyken
 * edit ekranı izinli personeli "uygun" gösteriyordu. Artık ikisi de bunu kullanır.
 *
 * @param candidates personel adayları (id, name, status, ...)
 * @param eligibilityMap {personnel_id: {is_unavailable, status_text, overlap_leave}}
 * @returns her aday + {isUnavailableForRange, unavailableText, overlapLeave}
 */
/** ISO "YYYY-MM-DD" → Türkçe "GG.AA.YYYY" (2026-08-11 → 11.08.2026). */
export const formatTrShortDate = (iso) => {
  const s = String(iso || "").slice(0, 10);
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  return m ? `${m[3]}.${m[2]}.${m[1]}` : s;
};

export const annotateSubstituteEligibility = (candidates, eligibilityMap = {}) => {
  const map = eligibilityMap || {};
  return (Array.isArray(candidates) ? candidates : []).map((p) => {
    const info = map[String(p?.id)] || {};
    const overlap = info.overlap_leave || null;
    const statusLower = String(p?.status || "").toLowerCase();
    const isUnavailable = Boolean(
      info.is_unavailable || statusLower === "on_leave" || statusLower === "on_report"
    );
    // Tarihi Türkçe GG.AA.YYYY göster; "izinde" sözcüğü metnin İÇİNDE TEK kez geçer
    // (edit dialog'ları artık "(izinde: …)" değil "(…)" ile sarıyor → çift "izinde" yok).
    // overlap_leave'i status_text'e tercih et (backend status_text ISO tarih + "izinde").
    let statusText = "";
    if (overlap?.start_date && overlap?.end_date) {
      statusText = `${formatTrShortDate(overlap.start_date)} - ${formatTrShortDate(overlap.end_date)} izinde`;
    } else if (info.status_text) {
      statusText = info.status_text;
    } else if (statusLower === "on_leave" || statusLower === "on_report") {
      statusText = "izinli/raporlu";
    }
    return {
      ...p,
      isUnavailableForRange: isUnavailable,
      unavailableText: statusText,
      overlapLeave: overlap,
    };
  });
};

/**
 * Aktif vekalet döneminde izin türü kuralı (Seçenek A — bağlayıcı):
 *   Yıllık İzin = BLOCK, Sosyal İzin = BLOCK, Rapor = ALLOW,
 *   Diğer = ALLOW ama uyarı + açıklama zorunlu (açıklama zorunluluğu çağırende enforce edilir).
 * `conflictRange` yoksa (çakışma yok) her tür geçerlidir.
 * Dönen: { valid, error, warning }.
 */
export const evaluateSubstitutionLeaveConflict = (reasonType, conflictRange) => {
  if (!conflictRange) return { valid: true, error: null, warning: null };
  const raw = String(reasonType || "").trim();
  const canon = raw ? canonicalizeReasonType(raw) : "";
  if (canon === "Rapor") {
    return { valid: true, error: null, warning: null };
  }
  if (canon === "Diğer") {
    return {
      valid: true,
      error: null,
      warning: `Seçtiğiniz tarihlerde aktif vekalet göreviniz var (${conflictRange}). Acil/özel durum nedeniyle talep oluşturabilirsiniz; açıklama zorunludur. Talebiniz onaylanırsa ilgili vekalet görevinin yeniden atanması gerekir.`,
    };
  }
  // Yıllık/Sosyal veya henüz tür seçilmemiş → engelle.
  return {
    valid: false,
    error: `Bu tarihlerde vekalet göreviniz var (${conflictRange}); yıllık/sosyal izin kullanamazsınız.`,
    warning: null,
  };
};

/**
 * Kullanılan sosyal izin günü: personel sayaç alanı ile onaylı sosyal
 * taleplerden türetilen toplamın büyüğü. Sayaç stale olsa bile (seed verisi
 * sayacı güncellemez) doğru değeri verir ve tüm yüzeylerde aynı sonucu üretir.
 */
export const computeSocialUsedDays = (personnel, personnelLeaveRequests = []) => {
  const approvedSocial = (personnelLeaveRequests || []).reduce((sum, lr) => {
    if (String(lr?.status || "").toLowerCase() !== "approved") return sum;
    if (canonicalizeReasonType(lr?.reason_type) !== "Sosyal İzin") return sum;
    return sum + resolveLeaveUsedDays(lr);
  }, 0);
  const field = Number(personnel?.used_social_leave_days) || 0;
  return Math.max(field, approvedSocial);
};
